import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, SimpleGrid, Icon, Button, Flex, Progress, Badge, Stack, Divider } from "@chakra-ui/react";
import { FaBullseye, FaCalendarAlt, FaRocket, FaCheckCircle, FaHome, FaMoneyBillWave, FaTrophy, FaClock } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PropertyStrategyGoals: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/strategy/goals"
        fallbackTitle="Goal-Based Property Planning"
        fallbackDescription="Achieve your property investment goals with tailored strategies"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg="linear-gradient(135deg, #10b981 0%, #059669 100%)" color="white" py={20}>
        <Container maxW="container.xl">
          <Flex direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={12}>
            <VStack align={{ base: "center", md: "flex-start" }} spacing={6} flex={1} textAlign={{ base: "center", md: "left" }}>
              <Badge bg="green.400" color="white" px={4} py={2} borderRadius="full" fontSize="sm">
                Retirement-Focused Strategy
              </Badge>
              <Heading size="3xl" fontWeight="black" lineHeight="1.2">
                Plan Your Retirement Beyond Super
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.900" lineHeight="1.8">
                If you're concerned your superannuation won't deliver the retirement lifestyle you want, property investment offers a proven path to financial security. Let's build a plan that works alongside your super—not instead of it.
              </Text>
            </VStack>
            <Box bg="whiteAlpha.200" p={8} borderRadius="2xl" backdropFilter="blur(10px)" minW="300px">
              <VStack spacing={6}>
                <Icon as={FaBullseye} boxSize={20} />
                <VStack spacing={2}>
                  <Text fontSize="3xl" fontWeight="bold">87%</Text>
                  <Text fontSize="sm" textAlign="center" color="whiteAlpha.900">
                    of clients achieve their goals within projected timeline
                  </Text>
                </VStack>
              </VStack>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Goal Framework */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">Our Retirement Planning Framework</Heading>
          <Text fontSize="lg" color="gray.600" maxW="3xl">
            A clear, structured approach for high-income earners who want long-term wealth building—not quick wins or unnecessary complexity
          </Text>
        </VStack>

        <Stack direction={{ base: "column", lg: "row" }} spacing={8}>
          <Box flex={1} bg="green.50" p={8} borderRadius="2xl" border="2px solid" borderColor="green.200">
            <HStack mb={6}>
              <Box bg="green.500" p={3} borderRadius="lg">
                <Icon as={FaHome} boxSize={6} color="white" />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Text fontWeight="bold" fontSize="lg" color="gray.800">Step 1</Text>
                <Text fontSize="sm" color="gray.600">Discovery</Text>
              </VStack>
            </HStack>
            <Heading size="md" mb={4} color="green.700">Understand Your Retirement Gap</Heading>
            <Text color="gray.600" lineHeight="1.7" mb={4}>
              We help you clarify the gap between your super projection and your desired retirement lifestyle—then show how property can bridge it.
            </Text>
            <VStack align="stretch" spacing={2}>
              <HStack>
                <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                <Text fontSize="sm">Super vs retirement needs analysis</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                <Text fontSize="sm">Desired retirement lifestyle mapping</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                <Text fontSize="sm">Current tax position review</Text>
              </HStack>
            </VStack>
          </Box>

          <Box flex={1} bg="blue.50" p={8} borderRadius="2xl" border="2px solid" borderColor="blue.200">
            <HStack mb={6}>
              <Box bg="blue.500" p={3} borderRadius="lg">
                <Icon as={FaRocket} boxSize={6} color="white" />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Text fontWeight="bold" fontSize="lg" color="gray.800">Step 2</Text>
                <Text fontSize="sm" color="gray.600">Strategy</Text>
              </VStack>
            </HStack>
            <Heading size="md" mb={4} color="blue.700">Build Your Tax-Efficient Strategy</Heading>
            <Text color="gray.600" lineHeight="1.7" mb={4}>
              Develop a property investment strategy that minimises tax now while building the passive income you'll need in retirement.
            </Text>
            <VStack align="stretch" spacing={2}>
              <HStack>
                <Icon as={FaCheckCircle} color="blue.500" boxSize={4} />
                <Text fontSize="sm">Tax-optimised structure planning</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="blue.500" boxSize={4} />
                <Text fontSize="sm">Portfolio growth milestones</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="blue.500" boxSize={4} />
                <Text fontSize="sm">Clear action plan with accountability</Text>
              </HStack>
            </VStack>
          </Box>

          <Box flex={1} bg="purple.50" p={8} borderRadius="2xl" border="2px solid" borderColor="purple.200">
            <HStack mb={6}>
              <Box bg="purple.500" p={3} borderRadius="lg">
                <Icon as={FaTrophy} boxSize={6} color="white" />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Text fontWeight="bold" fontSize="lg" color="gray.800">Step 3</Text>
                <Text fontSize="sm" color="gray.600">Execution</Text>
              </VStack>
            </HStack>
            <Heading size="md" mb={4} color="purple.700">Execute With Confidence</Heading>
            <Text color="gray.600" lineHeight="1.7" mb={4}>
              Move forward knowing you're making the right decisions—with ongoing guidance and regular check-ins to keep you on track.
            </Text>
            <VStack align="stretch" spacing={2}>
              <HStack>
                <Icon as={FaCheckCircle} color="purple.500" boxSize={4} />
                <Text fontSize="sm">Quarterly portfolio reviews</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="purple.500" boxSize={4} />
                <Text fontSize="sm">Market condition adjustments</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="purple.500" boxSize={4} />
                <Text fontSize="sm">Retirement readiness tracking</Text>
              </HStack>
            </VStack>
          </Box>
        </Stack>
      </Container>

      {/* Timeline Examples */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color="gray.800">Your Path to Retirement Security</Heading>
            <Text fontSize="lg" color="gray.600" maxW="3xl">
              See how property portfolios complement super to create the retirement lifestyle you want
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
            {/* Short-term Goal */}
            <Box bg="white" p={8} borderRadius="2xl" boxShadow="lg">
              <HStack mb={6} justify="space-between">
                <VStack align="flex-start" spacing={1}>
                  <Heading size="md" color="gray.800">First Investment Property</Heading>
                  <Text fontSize="sm" color="gray.600">3-5 Year Timeline</Text>
                </VStack>
                <Icon as={FaClock} boxSize={8} color="green.500" />
              </HStack>

              <VStack align="stretch" spacing={6}>
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Year 1: Foundation</Text>
                    <Badge colorScheme="green">Complete</Badge>
                  </HStack>
                  <Progress value={100} colorScheme="green" size="sm" borderRadius="full" mb={2} />
                  <Text fontSize="xs" color="gray.600">Build deposit, improve credit score, research markets</Text>
                </Box>

                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Year 2-3: Acquisition</Text>
                    <Badge colorScheme="blue">In Progress</Badge>
                  </HStack>
                  <Progress value={60} colorScheme="blue" size="sm" borderRadius="full" mb={2} />
                  <Text fontSize="xs" color="gray.600">Pre-approval, property search, purchase settlement</Text>
                </Box>

                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Year 4-5: Optimization</Text>
                    <Badge colorScheme="gray">Upcoming</Badge>
                  </HStack>
                  <Progress value={0} colorScheme="gray" size="sm" borderRadius="full" mb={2} />
                  <Text fontSize="xs" color="gray.600">Tenant management, value-add improvements, refinance options</Text>
                </Box>

                <Divider />

                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="bold" color="gray.700">Projected Value</Text>
                  <Text fontSize="xl" fontWeight="bold" color="green.600">$650K+</Text>
                </HStack>
              </VStack>
            </Box>

            {/* Long-term Goal */}
            <Box bg="white" p={8} borderRadius="2xl" boxShadow="lg">
              <HStack mb={6} justify="space-between">
                <VStack align="flex-start" spacing={1}>
                  <Heading size="md" color="gray.800">Multi-Property Portfolio</Heading>
                  <Text fontSize="sm" color="gray.600">10+ Year Timeline</Text>
                </VStack>
                <Icon as={FaMoneyBillWave} boxSize={8} color="blue.500" />
              </HStack>

              <VStack align="stretch" spacing={6}>
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Years 1-3: First Property</Text>
                    <Badge colorScheme="green">Complete</Badge>
                  </HStack>
                  <Progress value={100} colorScheme="green" size="sm" borderRadius="full" mb={2} />
                  <Text fontSize="xs" color="gray.600">Establish foundation with first investment property</Text>
                </Box>

                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Years 4-7: Portfolio Growth</Text>
                    <Badge colorScheme="blue">In Progress</Badge>
                  </HStack>
                  <Progress value={45} colorScheme="blue" size="sm" borderRadius="full" mb={2} />
                  <Text fontSize="xs" color="gray.600">Add 2-3 properties using equity from first property</Text>
                </Box>

                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Years 8-10+: Wealth Creation</Text>
                    <Badge colorScheme="gray">Upcoming</Badge>
                  </HStack>
                  <Progress value={0} colorScheme="gray" size="sm" borderRadius="full" mb={2} />
                  <Text fontSize="xs" color="gray.600">Scale to 5+ properties, passive income generation</Text>
                </Box>

                <Divider />

                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="bold" color="gray.700">Projected Portfolio Value</Text>
                  <Text fontSize="xl" fontWeight="bold" color="blue.600">$3.2M+</Text>
                </HStack>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Goal Categories */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">What High-Income Earners Want to Achieve</Heading>
          <Text fontSize="lg" color="gray.600" maxW="3xl">
            Property investment helps solve the challenges that super alone can't address
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          <Box bg="white" p={6} borderRadius="xl" border="2px solid" borderColor="green.100" _hover={{ borderColor: "green.300", transform: "translateY(-4px)" }} transition="all 0.3s">
            <Icon as={FaMoneyBillWave} boxSize={10} color="green.500" mb={4} />
            <Heading size="md" mb={3} color="gray.800">Passive Income in Retirement</Heading>
            <Text color="gray.600" fontSize="sm" lineHeight="1.7">
              Create reliable rental income that supplements super drawdowns—giving you flexibility and security in retirement
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="xl" border="2px solid" borderColor="blue.100" _hover={{ borderColor: "blue.300", transform: "translateY(-4px)" }} transition="all 0.3s">
            <Icon as={FaTrophy} boxSize={10} color="blue.500" mb={4} />
            <Heading size="md" mb={3} color="gray.800">Tax Minimisation Now</Heading>
            <Text color="gray.600" fontSize="sm" lineHeight="1.7">
              Reduce your tax bill legally while building equity—property offers deductions that other investments can't match
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="xl" border="2px solid" borderColor="purple.100" _hover={{ borderColor: "purple.300", transform: "translateY(-4px)" }} transition="all 0.3s">
            <Icon as={FaHome} boxSize={10} color="purple.500" mb={4} />
            <Heading size="md" mb={3} color="gray.800">Super Diversification</Heading>
            <Text color="gray.600" fontSize="sm" lineHeight="1.7">
              Reduce reliance on market-linked super—property provides a tangible asset that doesn't fluctuate with share markets
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="xl" border="2px solid" borderColor="orange.100" _hover={{ borderColor: "orange.300", transform: "translateY(-4px)" }} transition="all 0.3s">
            <Icon as={FaRocket} boxSize={10} color="orange.500" mb={4} />
            <Heading size="md" mb={3} color="gray.800">Early Retirement Option</Heading>
            <Text color="gray.600" fontSize="sm" lineHeight="1.7">
              Build enough passive income to retire before accessing super—giving you freedom and flexibility in your 50s
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="xl" border="2px solid" borderColor="red.100" _hover={{ borderColor: "red.300", transform: "translateY(-4px)" }} transition="all 0.3s">
            <Icon as={FaCalendarAlt} boxSize={10} color="red.500" mb={4} />
            <Heading size="md" mb={3} color="gray.800">Intergenerational Wealth</Heading>
            <Text color="gray.600" fontSize="sm" lineHeight="1.7">
              Build a legacy portfolio to pass on to future generations
            </Text>
          </Box>

          <Box bg="white" p={6} borderRadius="xl" border="2px solid" borderColor="teal.100" _hover={{ borderColor: "teal.300", transform: "translateY(-4px)" }} transition="all 0.3s">
            <Icon as={FaBullseye} boxSize={10} color="teal.500" mb={4} />
            <Heading size="md" mb={3} color="gray.800">Specific Purchase Goal</Heading>
            <Text color="gray.600" fontSize="sm" lineHeight="1.7">
              Fund a specific goal like children's education or dream home purchase
            </Text>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA Section */}
      <Box bg="green.600" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaBullseye} boxSize={16} />
            <Heading size="xl" maxW="2xl">
              Worried Your Super Won't Be Enough?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="whiteAlpha.900">
              You're earning well now—but will it translate into the retirement you want? Let's map out how property can bridge the gap between your super projection and your lifestyle goals.
            </Text>
            <Button size="lg" bg="white" color="green.600" _hover={{ bg: "gray.100" }} px={8}>
              Book Your Strategy Session
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyStrategyGoals;
